import {IPayWithGatewayTransaction} from "../../../@pano-project/types/transaction";

export enum EPaymentInstrument {
  Card = 'card',
  GooglePay = 'google_pay',
  ApplePay = 'apple_pay',
}

export type TPaymentInstrument = EPaymentInstrument | null;

export type TDigitalWalletProvider = EPaymentInstrument.GooglePay | EPaymentInstrument.ApplePay;

export interface IBillingAddress {
  full_name: string;
  country: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  phone: string;
  email: string;
}

export interface IDigitalWalletTokenizedSuccess {
  event: 'onDigitalWalletTokenized';
  tokenized: true;
  token_provider: TDigitalWalletProvider;
  token_data: {
    billing_address: IBillingAddress;
  };
  id: string;
  hpf_auth_token?: string;
}

export interface IDigitalWalletTokenizedFail {
  event: 'onDigitalWalletTokenized';
  tokenized: false;
  token_provider: TDigitalWalletProvider;
  token_data: null;
}

export interface GooglePaySuccess extends IDigitalWalletTokenizedSuccess {
  token_provider: EPaymentInstrument.GooglePay;
  hpf_auth_token: string;
}

export interface ApplePaySuccess extends IDigitalWalletTokenizedSuccess {
  token_provider: EPaymentInstrument.ApplePay;
  hpf_auth_token: string;
}

export type TDigitalWalletTokenizedEvent = GooglePaySuccess | ApplePaySuccess | IDigitalWalletTokenizedFail;

export interface IUseDigitalWallets {
  isPossible: boolean;
  isSelected: boolean;
  picInitiated: boolean;
  digitalWalletError: string | null;
  paymentInstrument: TPaymentInstrument;
  digitalWalletTokenizedData: TDigitalWalletTokenizedEvent,
  digitalBillingAddress: IBillingAddress,
  currencyList: ITokenizationProviderOptions['currency_list'];
  cardTokenizationOptions: ICardTokenizationOptions,
  onToggleWalletOrCard: (value: TPaymentInstrument) => void;
  onDigitalWalletTokenized: (data: TDigitalWalletTokenizedEvent, onSubmitCallback: any) => void;
  sendTransactionResults: (transaction: Partial<IPayWithGatewayTransaction>, fallbackMsg: string) => void;
  resetDigitalWalletError: () => void;
  getProviderName: (name: TPaymentInstrument, viewType?: 'full' | 'short') => string;
  onGetTransactionDetails: (payload: IGetTransactionDetails) => void;
  onDigitalWalletWindowClosed: () => void;
  onTokenizationAvailabilityEvent: (payload: ITokenizationAvailability) => void;
  shakeAmount: () => void;
  onPciInit: (value: boolean) => void;
  isAmountShaking: boolean;
}

export interface ITokenizationProviderOptions {
  token_provider: TDigitalWalletProvider;
  enabled: boolean;
  card_funding_sources: string[];
  card_types: string[];
  currency_list: {
    [key: string]: {
      max_amount: number;
      min_amount: number;
    };
  };
}

// keyed by TDigitalWalletProvider - one entry per digital wallet provider supported by the gateway
export interface ICardTokenizationOptions {
  google_pay: ITokenizationProviderOptions | null;
  apple_pay: ITokenizationProviderOptions | null;
}

export interface ITokenizationAvailability {
  google_pay?: boolean;
  apple_pay?: boolean;
}

export type TPaymentCredentialStatus = 'paymentCredentialsAvailable' | 'paymentCredentialsUnavailable' | 'paymentCredentialStatusUnknown';

export interface IGetTransactionDetails {
  id: string | number;
  provider?: TDigitalWalletProvider;
  paymentCredentialStatus?: TPaymentCredentialStatus;
}

export interface IUseWalletProvider {
  isEnabled: boolean;
  isSelected: boolean;
  isReadyToPay: boolean;
  isTokenizationChecked: boolean;
  removeBtnFromDOM: boolean;
}
